// Atributos dos blocos de mídia (contrato do conteúdo Tiptap 1.0, seção 3.3) → props
// dos componentes. Atributo ausente ou de tipo inesperado vira o padrão; bloco sem o
// essencial (src, vídeo ou caminho válidos) retorna null e não renderiza.
import { resolveLink } from './link'
import { DOWNLOAD_ROUTE, safeFilePath, safeImageSrc } from './safeUrl'
import { embedUrl, videoFromUrl, videoTitle } from './video'

const EXTENSION = /^[a-z0-9]{1,10}$/i
const UNITS = [ 'B', 'KB', 'MB', 'GB' ]

const read = attrs => (attrs && typeof attrs === 'object' ? attrs : {})
const text = value => (typeof value === 'string' ? value : '')

// Sem regex: `/\/+$/` retrocede de forma quadrática numa sequência longa de barras
const trimTrailingSlashes = (value) => {
	let end = value.length
	while (end > 0 && value[end - 1] === '/') end--
	return value.slice(0, end)
}

// Inteiro positivo, aceito também como string numérica (atributo HTML)
const dimension = (value) => {
	const number = typeof value === 'string' && /^\d+$/.test(value.trim()) ? Number(value) : value
	return Number.isInteger(number) && number > 0 ? number : null
}

const figure = ({ src, alt = '', caption = '', title = null, width = null, height = null }) =>
	({ src, alt, caption, title, width, height })

export const figureProps = (attrs) => {
	const { src, alt, caption } = read(attrs)
	const safe = safeImageSrc(src)
	return safe ? figure({ src: safe, alt: text(alt), caption: text(caption).trim() }) : null
}

export const imageProps = (attrs) => {
	const { src, alt, title, width, height } = read(attrs)
	const safe = safeImageSrc(src)
	return safe ? figure({ src: safe, alt: text(alt), title: text(title) || null, width: dimension(width), height: dimension(height) }) : null
}

export const galleryImages = (attrs) => {
	const { images } = read(attrs)
	if (!Array.isArray(images)) return null
	return images.map(image => figureProps(image)).filter(Boolean).map(({ src, alt, caption }) => ({ src, alt, caption }))
}

export const imageLinkProps = (attrs, origin) => {
	const { src, alt, href } = read(attrs)
	const safe = safeImageSrc(src)
	return safe ? { src: safe, alt: text(alt), link: resolveLink(href, origin) } : null
}

// O embed sai de provider + videoId, nunca de `url` (contrato 3.3)
export const videoProps = (attrs) => {
	const { provider, videoId } = read(attrs)
	const src = embedUrl(provider, videoId)
	return src ? { src, title: videoTitle(provider) } : null
}

// Nó `youtube` nativo do Tiptap: só tem `src`, então o id sai dele
export const youtubeProps = (attrs) => {
	const video = videoFromUrl(read(attrs).src)
	return video?.provider === 'youtube' ? videoProps(video) : null
}

export const formatFileSize = (size) => {
	if (typeof size !== 'number' || !Number.isFinite(size) || size <= 0) return ''
	let value = size
	let unit = 0
	while (value >= 1024 && unit < UNITS.length - 1) {
		value /= 1024
		unit++
	}
	return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} ${UNITS[unit]}`
}

export const fileDownloadProps = (attrs, apiRoute) => {
	const { path, name, extension, size } = read(attrs)
	const safe = safeFilePath(path)
	if (!safe) return null
	const type = typeof extension === 'string' && EXTENSION.test(extension) ? extension.toUpperCase() : ''
	return {
		href: `${trimTrailingSlashes(String(apiRoute || ''))}${DOWNLOAD_ROUTE}${safe}`,
		label: text(name).trim() || 'Baixar arquivo',
		meta: [ type, formatFileSize(size) ].filter(Boolean).join(' · '),
	}
}
